'use client';

import { CheckIcon, LinkBreakIcon, TargetIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import type { CategoryOverviewRow } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGoalsOverview } from '@/hooks/queries/useGoalsOverview';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { getGoalIcon } from '@/components/metas/helpers';

interface GoalLinkPickerProps {
  /** The income row being linked; null keeps the picker closed. */
  row: CategoryOverviewRow | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  /** null goalId removes the link. */
  onSelect: (categoryRef: string, goalId: string | null) => void;
}

/**
 * Meta picker for an income category row (display-only link): the user's goals with
 * their live progress, plus "Remover vínculo" when one is already linked. Desktop =
 * centered modal, mobile = bottom sheet — the NewCategorySheet dual pattern.
 */
export function GoalLinkPicker({ row, saving, onOpenChange, onSelect }: GoalLinkPickerProps) {
  const isMobile = useIsMobile();
  const open = row != null;
  // Fetch lazily: the goals list only loads once the picker first opens.
  const { data, isLoading } = useGoalsOverview(open);
  const formatCurrency = useFormatCurrency();

  const goals = data?.goals ?? [];
  const linkedGoalId = row?.linkedGoal?.goalId ?? null;

  const body = row == null ? null : (
    <div className="flex flex-col gap-1 overflow-y-auto px-1 pb-2">
      {isLoading && goals.length === 0 ? (
        <div className="space-y-1.5 p-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : goals.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-text-tertiary">
          Você ainda não tem metas.{' '}
          <Link to="/metas" className="font-semibold text-brand-primary hover:underline">
            Criar uma meta
          </Link>
        </div>
      ) : (
        <>
          {goals.map((goal) => {
            const Icon = getGoalIcon(goal.icon, goal.type);
            const p = Math.max(0, Math.min(100, Math.round(goal.progressPercent)));
            const isLinked = goal.id === linkedGoalId;
            return (
              <button
                key={goal.id}
                type="button"
                disabled={saving}
                onClick={() => onSelect(row.categoryRef, goal.id)}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent disabled:opacity-50"
              >
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">
                  <Icon className="size-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-text-primary">
                    {goal.name}
                  </span>
                  <span className="mt-1 flex items-center gap-2">
                    <span className="h-1.5 max-w-40 flex-1 overflow-hidden rounded-full bg-muted">
                      <span
                        className="block h-full rounded-full bg-success"
                        style={{ width: `${p}%` }}
                      />
                    </span>
                    <span className="font-mono text-2xs text-text-tertiary tabular-nums">
                      {p}% de {formatCurrency(goal.targetAmount)}
                    </span>
                  </span>
                </span>
                {isLinked && <CheckIcon className="size-4 shrink-0 text-brand-primary" />}
              </button>
            );
          })}
          {linkedGoalId != null && (
            <button
              type="button"
              disabled={saving}
              onClick={() => onSelect(row.categoryRef, null)}
              className="mt-1 flex items-center gap-3 rounded-xl border-t border-border-subtle px-3 py-2.5 text-left text-error-text transition-colors hover:bg-accent disabled:opacity-50"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-error-surface">
                <LinkBreakIcon className="size-4.5" />
              </span>
              <span className="text-sm font-semibold">Remover vínculo</span>
            </button>
          )}
        </>
      )}
    </div>
  );

  const title = (
    <span className="flex items-center gap-2">
      <TargetIcon className="size-4.5 text-brand-700" />
      Vincular meta
    </span>
  );
  const description = row
    ? `O progresso da meta aparece na linha de ${row.name} — nada muda na meta em si.`
    : '';

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          {body}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
